import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";

// The Story 2.9 scaffold filled unbuilt destinations with "Coming in <epic>" bodies.
// Every one has since been replaced by a real screen or removed with its route, so the
// generic `PagePlaceholder` / `PublicPlaceholder` shells are gone too — only the
// catch-all below remains. The graveyard is kept as the record of where each went.

// Public / auth routes. /sign-in (Story 2.10) and /forced-password-change
// (Story 2.11) render their real screens; /register redirects to /sign-up/legacy
// (gh-755). PublicPlaceholder removed — it was left as a
// shared standalone auth-card shell for a future pre-shell route and never used again.

// In-shell destinations (later epics).
// WorkspacePlaceholder removed — /workspace now renders the real WorkspaceHomeScreen (Story 6.9).
// ClientsPlaceholder removed — /clients now renders the real ClientsListScreen (Story 5.7).
// EvaluationsPlaceholder removed — /evaluations now renders the real EvaluationsListScreen (Story 6.9).
// ResultsPlaceholder removed — /results now renders the real ResultsLibraryScreen (Story 9.13).
// CalibrationCasePlaceholder removed — /calibration/cases/:caseId now renders the
// real CalibrationCaseScoringScreen (Story 8.10, Screen 17), closing
// seam-87-calibration-scoring-view.
// CalibrationCaseLogPlaceholder removed — /calibration/cases/:caseId/log now renders
// the real CalibrationCaseLogScreen (Story 8.8, Screen 18).
// ProfilePlaceholder removed — /profile now renders the real ProfileScreen (Story 16.3).
// ResourcesPlaceholder removed — replaced by ResourcesScreen.
// AdminUsersPlaceholder removed — /admin/users now renders the real
// AdminUsersListScreen (Story 11.8, Screen 20), with /admin/users/:userId and
// /admin/users/new rendering Screens 21 and 22 (Stories 11.8 / 11.9). This closes
// seam-86-admin-card-mount by equivalent mount.
// AdminReadinessPlaceholder / AdminActivityPlaceholder / AdminSettingsPlaceholder
// removed with their routes and nav entries: no FR, story or PRD ever described a
// super_admin Readiness, Activity or Settings screen, and their "Epic 14 / 15 / 11"
// owners were an implementation-time guess in the 2.9 scaffold, never ratified. The
// client-validated admin sidebar (Screens 10 / 20) carries no such entries, and the
// product's only Settings is FR84's org_admin one at /org-admin/settings.

// Catch-all for unknown routes — standalone full-page layout (no AppShell).
export function CatchAllPlaceholder() {
  const location = useLocation();

  // pathname only — no search/hash; Franklin routes use UUID params, never PHI text in the path.
  useEffect(() => {
    Sentry.captureMessage(`404 Not Found: ${location.pathname}`, "warning");
  }, [location.pathname]);

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-[color:var(--bg-secondary)] p-8">
      <div className="flex w-full max-w-sm flex-col items-center gap-6 text-center">
        <div className="flex flex-col gap-2">
          <h1 className="display-xs text-[color:var(--text-primary)]">Page not found</h1>
          <p className="text-md text-[color:var(--text-tertiary)]">
            The page you&apos;re looking for doesn&apos;t exist or has been moved.
            Use the navigation to return to a known screen.
          </p>
        </div>
        <Button asChild variant="secondary">
          <Link to="/">Go to home</Link>
        </Button>
      </div>
    </main>
  );
}
